/** Date/time helpers for calendar forms. All conversions stay in local time. */

/** Local YYYY-MM-DD. Never use toISOString().slice(0,10) — it shifts the day. */
export function toLocalDateString(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Splits an ISO instant into the local calendar date and HH:MM shown in the form. */
export function splitLocalDateTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' }
  const d = new Date(iso)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return { date: toLocalDateString(d), time: `${h}:${m}` }
}

/** 24h HH:MM slots at the given step, e.g. 30 → 00:00, 00:30, … */
export function timeSlots(stepMin: number): string[] {
  const slots: string[] = []
  for (let m = 0; m < 24 * 60; m += stepMin) {
    const h = String(Math.floor(m / 60)).padStart(2, '0')
    const min = String(m % 60).padStart(2, '0')
    slots.push(`${h}:${min}`)
  }
  return slots
}

/**
 * End-time options from startTime + 15min up to +8h, labelled with the duration.
 * Values are `YYYY-MM-DDTHH:MM` so the form can build a Date directly.
 */
export function endTimeOptions(date: string, startTime: string): { value: string; label: string }[] {
  if (!date || !startTime) return timeSlots(15).map(t => ({ value: `${date}T${t}`, label: t }))
  const [sh, sm] = startTime.split(':').map(Number)
  const startTotal = sh * 60 + sm
  const opts: { value: string; label: string }[] = []
  for (let add = 15; add <= 8 * 60; add += 15) {
    const total = startTotal + add
    if (total >= 24 * 60) break
    const h = String(Math.floor(total / 60)).padStart(2, '0')
    const min = String(total % 60).padStart(2, '0')
    const hours = add / 60
    const label = hours < 1
      ? `${h}:${min} (${add} min)`
      : `${h}:${min} (${hours % 1 === 0 ? hours : hours.toFixed(1)} h)`
    opts.push({ value: `${date}T${h}:${min}`, label })
  }
  return opts
}

/** "12 Aug 2026, 09:30" for a stored instant; empty string for null. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

/** "12 Aug 2026" for a plain YYYY-MM-DD, parsed as local (not UTC). */
export function formatDateOnly(date: string | null, style: 'medium' | 'full' = 'medium'): string {
  if (!date) return ''
  const [y, m, d] = date.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString([], { dateStyle: style })
}
